<template lang="pug">
  modal.modal(:title='confirmSelection ? $t("folders.shareModal.confirmTitle") : $t("folders.shareModal.title")' @closeModal='hideModal')
    .width-100(v-if='confirmSelection')
      h2.text-normal.dark-gray.m-bottom-1.m-top-2 {{ $t('folders.shareModal.shareDescription') }}
      ul.selected-list
        li.text-normal.dark-gray(v-for='user in selectedUsers' :key='user.id')
          | {{ getSelectedUserText(user) }}
      .row.middle.center.m-top-2
        button.secondary-button.m-right-1(@click='setConfirmSelection(false)') {{ $t('folders.shareModal.cancel') }}
        button.primary-button.with-icon.m-left-1(type='button' @click='shareFolderWithUsers')
          simple-svg.white-icon.m4-right-2(:filepath='shareIcon' width='20px' height='20px')
          | {{ $t('folders.share') }}
    .width-100(v-else)
      p.dark-gray.text-normal.m4-top-2.m-bottom-2 {{ $t('folders.shareModal.description') }}
      share-modal-filters.m-bottom-1(@search='filterUsers')
      share-modal-list(
        source="folderShareModal"
        :sourceProps='props'
        @selectAll='toggleAll'
        :loading='loadingUsers'
        :users='usersList'
        :selectedUsers='selectedUsers'
      )
        .user(v-for='user in usersList' :key='user.id')
          .option.row.middle.width-100
            checkbox.filter.m-right-1(
              v-if='!user.folderAccess'
              name='userSelected'
              transparent
              :disabled='user.folderAccess'
              :key='user.id'
              :checked='isChecked(user.id)'
              :updateCheck='() => selectUser(user)'
            )
            img.avatar(v-if='!user.isGroup' :src='user.avatar || defaultProfileImage')
            .group-avatar.row.middle.center(v-else)
              simple-svg(:filepath='groupIcon' width='20px' height='20px')
            .column.m-left-1.item-1
              .row.middle.ellipsis.username
                h2.text-normal.m-right-05 {{ user.name }}
                h2.text-normal.ellipsis(
                  v-if='!user.isGroup && (user.position.positionId || user.schoolName)'
                  :title='getPositionWithSchoolName(user.position, user.schoolName)'
                ) {{ getPositionWithSchoolName(user.position, user.schoolName) }}
              h4.text-small.dark-gray(v-if='!user.isGroup') {{ user.email }}
            button.text-normal.black.m-right-1(
              v-if='user.isGroup && !isChecked(user.id)'
              @click='() => showEditSharingGroupModal(user.id)'
            ) {{ $t('createSharingGroupModal.editGroup') }}
            .permissions-select.m-left-2.dark-gray(v-if='user.folderPermission === FOLDER_PERMISSIONS.owner')
              | {{ $t("folders.shareModal.owner") }}
            .permissions-select.m-left-2(v-else-if='user.folderAccess || isChecked(user.id)')
              k-select.width-100(
                name='userPermission'
                small noSpacing transparent
                labelName='label'
                :withBorder='false'
                @selectOption='(name, value) => editPermissions(user, value)'
                :preselectedOption='getPermission(user)'
                :options='user.isGroup ? [...permissionsOptions, ...sharingGroupOptions(user.id)] : permissionsOptions'
              )
      .row.end.m4-top-7
        button.primary-button.with-icon(type='button' @click='setConfirmSelection(true)')
          simple-svg.white-icon.m4-right-2(:filepath='shareIcon' width='20px' height='20px')
          | {{ $t('folders.share') }}
</template>

<script>
import Checkbox from '@/components/Inputs/Checkbox'
import defaultProfileImage from '@/assets/empty_profile.svg'
import ShareModalFilters from '@/components/ShareModalFilters'
import ShareModalList from '@/components/ShareModalList'
import { mapActions, mapState } from 'vuex'
import KSelect from '@/components/KSelect'
import amplitude from 'amplitude-js'
import groupIcon from '@/assets/ic_group.svg'
import Modal from '@/components/Modal'
import shareIcon from '../../assets/ic_share.svg'
import { folderPermissionsOptions, folderPermissionsIcons, FOLDER_PERMISSIONS } from '../../constants/folders'
import { i18n } from '../../main'
import { updateUserPermissions } from '../../services/FolderService'
import userPosition from '@/mixins/userPosition'

export default {
  mixins: [userPosition],
  components: { KSelect, Modal, ShareModalList, ShareModalFilters, Checkbox },
  data() {
    return {
      groupIcon,
      shareIcon,
      defaultProfileImage,
      FOLDER_PERMISSIONS,
      loadingUsers: true,
      memberSearch: '',
      selectedUsers: [],
      availableUsers: [],
      filters: {},
      confirmSelection: false
    }
  },
  computed: {
    ...mapState({
      props: state => state.behavior.folderShareModal.props,
      users: state => state.folders.availableUsersToShareFolder,
      mySharingGroups: state => state.sharingGroup.mySharingGroups,
      userInfo: state => state.userInfo.userInfo,
      locale: state => state.behavior.locale
    }),
    hasEditPermission() {
      return this.props.hasEditPermission
    },
    permissionsOptions() {
      const options = Object.keys(folderPermissionsOptions)
        .map(key => ({
          label: `
            <div class='row'>
              <img class='m4-right-2' src='${folderPermissionsIcons[key]}'/>
              <p class='text-normal'>${i18n.t('folders.' + key)}</p>
            </div>
          `,
          id: folderPermissionsOptions[key]
        }))
      return options
    },
    usersList() {
      const list = [
        ...this.users.map(user => ({
          id: user.userId,
          name: user.fullName,
          avatar: user.avatar,
          position: user.position,
          folderAccess: user.folderAccess,
          folderPermission: user.folderPermission,
          schoolName: user.schoolName,
          email: user.email,
          isGroup: false
        })),
        ...this.mySharingGroups.map(group => ({
          id: group.id,
          name: group.name,
          folderAccess: null,
          folderPermission: null,
          users: group.userSharingGroups,
          isGroup: true
        }))
      ]
      return list
        .sort((a, b) => a.name.localeCompare(b.name))
        .sort((a, b) => (a.folderAccess === b.folderAccess) ? 0 : a.folderAccess ? -1 : 1)
    }
  },
  async created() {
    this.filters = {
      schoolId: this.userInfo.schoolId !== 0 ? this.userInfo.schoolId : null,
      folderId: this.props.id
    }
    await this.getAvailableUsersToShareFolder(this.filters)
    await this.hydrateMySharingGroups()
    this.loadingUsers = false
  },
  methods: {
    ...mapActions([
      'getAvailableUsersToShareFolder',
      'clearAvailableUsersToShareFolder',
      'hydrateMySharingGroups',
      'showElement',
      'hideElement',
      'shareFolder'
    ]),
    getPermission(user) {
      return user.folderAccess
        ? user.folderPermission
        : this.selectedUsers.find(u => u.id === user.id).folderPermission
    },
    sharingGroupOptions(groupId) {
      return [
        {
          label: i18n.t('createSharingGroupModal.editGroup'),
          onClick: () => this.showEditSharingGroupModal(groupId)
        }
      ]
    },
    setConfirmSelection(value) {
      this.confirmSelection = value
    },
    getSelectedUserText(user) {
      return user.isGroup
        ? user.name
        : `${user.name} ${this.getPositionWithSchoolName(user.position, user.schoolName)}`
    },
    hideModal() {
      this.hideElement({ element: 'folderShareModal' })
      this.clearAvailableUsersToShareFolder()
    },
    isChecked(id) {
      return Boolean(this.selectedUsers.find(us => us.id === id))
    },
    selectUser(user) {
      if (this.isChecked(user.id)) {
        this.selectedUsers = this.selectedUsers.filter(us => us.id !== user.id)
      } else {
        this.selectedUsers = [...this.selectedUsers, { ...user, folderPermission: folderPermissionsOptions.edit }]
      }
    },
    editPermissions(user, permission) {
      if (user.folderAccess) return this.editPermissionsAsync(user, permission)
      const selIndex = this.selectedUsers.findIndex(us => us.id === user.id)
      this.selectedUsers[selIndex].folderPermission = permission
    },
    async editPermissionsAsync(user, permission) {
      if (permission) return updateUserPermissions(this.props.id, { user, permission })
      this.hideElement({ element: 'folderShareModal' })
      this.showElement({
        element: 'choiceModal',
        props: {
          title: i18n.t('folders.removeAccessModal.title'),
          message: `
              <h2 class="text-normal m-bottom-2">${i18n.t('folders.removeAccessModal.subtitle')}</h2>
              <ul style="padding-left: 20px;">
                <li style="list-style-type: disc;">${user.name || user.email}</li>
              </ul>
            `,
          rightFunc: async () => {
            await updateUserPermissions(this.props.id, {
              user: { userId: user.id },
              permission: 0
            }).catch(e => e.response)
            this.hideElement({ element: 'choiceModal' })
            this.showElement({
              element: 'folderShareModal',
              props: {
                id: this.props.id,
                name: this.props.name,
                hasEditPermission: this.hasEditPermission
              }
            })
          },
          leftFunc: () => {
            this.hideElement({ element: 'choiceModal' })
            this.showElement({
              element: 'folderShareModal',
              props: {
                id: this.props.id,
                name: this.props.name,
                hasEditPermission: this.hasEditPermission
              }
            })
          }
        }
      })
    },
    toggleAll() {
      if (this.selectedUsers.length) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = [...this.usersList.map(user => ({ ...user, folderPermission: FOLDER_PERMISSIONS.read }))]
      }
    },
    showEditSharingGroupModal(groupId) {
      this.hideElement({ element: 'folderShareModal' })
      this.showElement({
        element: 'editSharingGroupModal',
        props: { source: 'folderShareModal', sourceProps: this.props, groupId }
      })
    },
    async filterUsers(filters) {
      this.selectedUsers = []
      this.loadingUsers = true
      this.filters = { ...this.filters, ...filters }
      await this.getAvailableUsersToShareFolder(this.filters)
      this.availableUsers = this.users.map(user => ({ ...user, permission: FOLDER_PERMISSIONS.edit }))
      this.loadingUsers = false
    },
    async shareFolderWithUsers() {
      this.showElement({ element: 'fullPageLoader' })
      const users = []
      this.selectedUsers = this.selectedUsers.sort((a, b) => a.isGroup - b.isGroup)
      for (const user of this.selectedUsers) {
        if (!user.isGroup) users.push({ email: user.email, permissionName: user.folderPermission })
        else {
          for (const member of user.users) {
            if (!users.find(u => u.email === member.user.email)) {
              users.push({ email: member.user.email, permissionName: user.folderPermission })
            }
          }
        }
      }
      const success = await this.shareFolder({ id: this.props.id, users, locale: this.locale })
      amplitude.getInstance().logEvent('Share Folder')
      this.hideModal()
      this.hideElement({ element: 'fullPageLoader' })
      if (success) {
        this.showElement({
          element: 'successModal',
          props: { title: i18n.t('folders.shareModal.success') }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variables/_colors.scss';

.modal{
  --max-width: 760px;
}

.users {
  height: 300px;
  overflow-y: scroll;
  &>:nth-child(even){
    background-color: $light-gray;
  }
  .filter {
    .label {
      font-size: 16px;
      line-height: 19px;
    }
  }
}

.user-search {
  border: 1px solid $gray;
  border-radius: 6px;
  padding: 0 8px;
  position: relative;

  .input-base {
    margin: 0;
  }
}

.option{
  padding: 10px;
  height: 50px;
  .username{
    max-width: 420px;
  }
  .avatar{
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
  }
  .group-avatar{
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid $gray;
  }
  .permissions-select{
    width: 160px;
    --font-size: 14px;
  }
}

.selected-list{
  padding-left: 20px;
  li{
    list-style: disc;
  }
}
</style>
